import { KeyboardEvent, SyntheticEvent } from 'react'

/**
 * Keyboard handler that fires `onActivate` on Enter or Space, matching what a
 * real `<button>` does. Space is prevented from its default (scrolling the
 * page) once it activates.
 */
export const activateOnEnterOrSpace =
  (onActivate: () => void) =>
  (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onActivate()
  }

/**
 * The full prop set that turns a non-interactive element into a keyboard- and
 * screen-reader-accessible button. Spread onto the drill-down labels and
 * artwork in the tables, cards, and info strips:
 *
 * `<Typography sx={linkTypographySx} {...clickableProps(() => openGame(id))}>`
 *
 * Needed because most of UA Insights drills down from text and icons rather
 * than from buttons, and a bare `onClick` leaves those targets invisible to
 * keyboard and screen-reader users.
 *
 * @param options Set `stopPropagation` when the target sits inside another
 * clickable element (a game icon within a clickable pattern row, say) so
 * activating it doesn't also trigger the row behind it.
 */
export const clickableProps = (onActivate: () => void, options?: { stopPropagation?: boolean }) => {
  const activate = (event: SyntheticEvent): void => {
    if (options?.stopPropagation) event.stopPropagation()
    onActivate()
  }

  return {
    role: 'button',
    tabIndex: 0,
    onClick: activate,
    onKeyDown: (event: KeyboardEvent): void => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      activate(event)
    },
  }
}

/**
 * {@link clickableProps} for cards and rows whose drill-down is optional. When
 * no handler is given the element gets no button role and stays out of the tab
 * order, so a decorative card isn't announced as an interactive one.
 */
export const optionalClickableProps = (onActivate?: () => void) => (onActivate ? clickableProps(onActivate) : {})
